/*!
 * Copyright © 2005-2025 Hyland Software, Inc. and its affiliates. All rights reserved.
 *
 * Alfresco Example Content Application
 *
 * This file is part of the Alfresco Example Content Application.
 * If the software was purchased under a paid Alfresco license, the terms of
 * the paid license agreement will prevail. Otherwise, the software is
 * provided under the following open source license terms:
 *
 * The Alfresco Example Content Application is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Lesser General Public License as published by
 * the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.
 *
 * The Alfresco Example Content Application is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
 * GNU Lesser General Public License for more details.
 *
 * You should have received a copy of the GNU Lesser General Public License
 * from Hyland Software. If not, see <http://www.gnu.org/licenses/>.
 */

/**
 * Tiny dependency-free .xlsx (Office Open XML) writer.
 *
 * Builds the minimal set of OOXML parts (using inline strings, so no shared
 * string table) and packs them into a STORED (uncompressed) ZIP — which is a
 * valid .xlsx that Excel/LibreOffice open natively, with full UTF-8 (Thai)
 * support. Avoids pulling in a heavy spreadsheet library.
 *
 * eslint-disable rationale: CRC32 and the ZIP binary headers are defined in
 * terms of bitwise ops, and the OOXML part templates are single long XML lines —
 * both are inherent to the file formats, not style choices.
 */
/* eslint-disable no-bitwise, max-len */

let crcTable: number[] | null = null;
function crc32(buf: Uint8Array): number {
  if (!crcTable) {
    crcTable = [];
    for (let n = 0; n < 256; n++) {
      let c = n;
      for (let k = 0; k < 8; k++) {
        c = c & 1 ? 0xedb88320 ^ (c >>> 1) : c >>> 1;
      }
      crcTable[n] = c >>> 0;
    }
  }
  let crc = 0xffffffff;
  for (let i = 0; i < buf.length; i++) {
    crc = (crc >>> 8) ^ crcTable[(crc ^ buf[i]) & 0xff];
  }
  return (crc ^ 0xffffffff) >>> 0;
}

const enc = new TextEncoder();
const u8 = (s: string): Uint8Array => enc.encode(s);

function xmlEscape(value: any): string {
  return String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function colRef(index: number): string {
  let s = '';
  let i = index + 1;
  while (i > 0) {
    const m = (i - 1) % 26;
    s = String.fromCharCode(65 + m) + s;
    i = Math.floor((i - 1) / 26);
  }
  return s;
}

function rowXml(cells: any[], rowNumber: number): string {
  // a finite number is a number cell, so a spreadsheet can add it up; anything else is text
  const tds = cells
    .map((c, ci) =>
      typeof c === 'number' && Number.isFinite(c)
        ? `<c r="${colRef(ci)}${rowNumber}"><v>${c}</v></c>`
        : `<c r="${colRef(ci)}${rowNumber}" t="inlineStr"><is><t xml:space="preserve">${xmlEscape(c)}</t></is></c>`
    )
    .join('');
  return `<row r="${rowNumber}">${tds}</row>`;
}

/** STORED-method ZIP of the given files. */
function zipStore(files: { name: string; data: Uint8Array }[]): Blob {
  const parts: Uint8Array[] = [];
  const central: Uint8Array[] = [];
  let offset = 0;

  for (const f of files) {
    const nameBytes = u8(f.name);
    const crc = crc32(f.data);
    const size = f.data.length;

    const lh = new Uint8Array(30 + nameBytes.length);
    const ldv = new DataView(lh.buffer);
    ldv.setUint32(0, 0x04034b50, true);
    ldv.setUint16(4, 20, true); // version needed
    ldv.setUint16(6, 0x0800, true); // flag: UTF-8 names
    ldv.setUint16(8, 0, true); // method: store
    ldv.setUint16(10, 0, true); // mod time
    ldv.setUint16(12, 0, true); // mod date
    ldv.setUint32(14, crc, true);
    ldv.setUint32(18, size, true); // compressed size
    ldv.setUint32(22, size, true); // uncompressed size
    ldv.setUint16(26, nameBytes.length, true);
    ldv.setUint16(28, 0, true); // extra length
    lh.set(nameBytes, 30);
    parts.push(lh, f.data);

    const cd = new Uint8Array(46 + nameBytes.length);
    const cdv = new DataView(cd.buffer);
    cdv.setUint32(0, 0x02014b50, true);
    cdv.setUint16(4, 20, true); // version made by
    cdv.setUint16(6, 20, true); // version needed
    cdv.setUint16(8, 0x0800, true); // flag UTF-8
    cdv.setUint16(10, 0, true); // method
    cdv.setUint16(12, 0, true); // time
    cdv.setUint16(14, 0, true); // date
    cdv.setUint32(16, crc, true);
    cdv.setUint32(20, size, true);
    cdv.setUint32(24, size, true);
    cdv.setUint16(28, nameBytes.length, true);
    cdv.setUint16(30, 0, true); // extra
    cdv.setUint16(32, 0, true); // comment
    cdv.setUint16(34, 0, true); // disk
    cdv.setUint16(36, 0, true); // internal attrs
    cdv.setUint32(38, 0, true); // external attrs
    cdv.setUint32(42, offset, true); // local header offset
    cd.set(nameBytes, 46);
    central.push(cd);

    offset += lh.length + size;
  }

  const centralSize = central.reduce((a, c) => a + c.length, 0);
  const eocd = new Uint8Array(22);
  const edv = new DataView(eocd.buffer);
  edv.setUint32(0, 0x06054b50, true);
  edv.setUint16(4, 0, true);
  edv.setUint16(6, 0, true);
  edv.setUint16(8, files.length, true);
  edv.setUint16(10, files.length, true);
  edv.setUint32(12, centralSize, true);
  edv.setUint32(16, offset, true);
  edv.setUint16(20, 0, true);

  return new Blob([...parts, ...central, eocd], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
  });
}

/** One sheet of a workbook: a header row and the data rows under it. */
export interface XlsxSheet {
  name: string;
  headers: any[];
  rows: any[][];
}

/** Builds a single-sheet .xlsx workbook from a header row + data rows. */
export function buildXlsx(sheetName: string, headers: any[], rows: any[][]): Blob {
  return buildWorkbook([{ name: sheetName, headers, rows }]);
}

/** Excel's rules for a sheet name: at most 31 characters, none of \ / ? * [ ] :, and no two alike. */
function sheetNames(sheets: XlsxSheet[]): string[] {
  const names: string[] = [];
  sheets.forEach((sheet, i) => {
    const base = (sheet.name || `Sheet${i + 1}`).replace(/[\\/?*[\]:]/g, '').slice(0, 31) || `Sheet${i + 1}`;
    let name = base;
    for (let n = 2; names.some((taken) => taken.toLowerCase() === name.toLowerCase()); n++) {
      name = `${base.slice(0, 31 - String(n).length - 1)} ${n}`;
    }
    names.push(name);
  });
  return names;
}

/** Builds an .xlsx workbook of several sheets, in the order given. */
export function buildWorkbook(sheets: XlsxSheet[]): Blob {
  const names = sheetNames(sheets);
  const sheetXml = (sheet: XlsxSheet): string => {
    let sheetData = rowXml(sheet.headers, 1);
    sheet.rows.forEach((r, i) => {
      sheetData += rowXml(r, i + 2);
    });
    return `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>\n<worksheet xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main"><sheetData>${sheetData}</sheetData></worksheet>`;
  };

  const overrides = sheets
    .map(
      (_, i) =>
        `<Override PartName="/xl/worksheets/sheet${i + 1}.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.worksheet+xml"/>`
    )
    .join('');
  const contentTypes = `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>\n<Types xmlns="http://schemas.openxmlformats.org/package/2006/content-types"><Default Extension="rels" ContentType="application/vnd.openxmlformats-package.relationships+xml"/><Default Extension="xml" ContentType="application/xml"/><Override PartName="/xl/workbook.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet.main+xml"/>${overrides}</Types>`;
  const rootRels = `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>\n<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships"><Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/officeDocument" Target="xl/workbook.xml"/></Relationships>`;
  const sheetEntries = names.map((name, i) => `<sheet name="${xmlEscape(name)}" sheetId="${i + 1}" r:id="rId${i + 1}"/>`).join('');
  const workbook = `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>\n<workbook xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main" xmlns:r="http://schemas.openxmlformats.org/officeDocument/2006/relationships"><sheets>${sheetEntries}</sheets></workbook>`;
  const relEntries = sheets
    .map(
      (_, i) =>
        `<Relationship Id="rId${i + 1}" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/worksheet" Target="worksheets/sheet${i + 1}.xml"/>`
    )
    .join('');
  const wbRels = `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>\n<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">${relEntries}</Relationships>`;

  return zipStore([
    { name: '[Content_Types].xml', data: u8(contentTypes) },
    { name: '_rels/.rels', data: u8(rootRels) },
    { name: 'xl/workbook.xml', data: u8(workbook) },
    { name: 'xl/_rels/workbook.xml.rels', data: u8(wbRels) },
    ...sheets.map((sheet, i) => ({ name: `xl/worksheets/sheet${i + 1}.xml`, data: u8(sheetXml(sheet)) }))
  ]);
}

/** Builds a workbook and triggers a browser download. */
export function downloadWorkbook(filename: string, sheets: XlsxSheet[]): void {
  save(buildWorkbook(sheets), filename.endsWith('.xlsx') ? filename : `${filename}.xlsx`);
}

/** Hands the browser a file to save. */
export function save(blob: Blob, filename: string): void {
  const a = document.createElement('a');
  a.href = URL.createObjectURL(blob);
  a.download = filename;
  a.click();
  URL.revokeObjectURL(a.href);
}

/** Builds the .xlsx and triggers a browser download. */
export function downloadXlsx(filename: string, sheetName: string, headers: any[], rows: any[][]): void {
  save(buildXlsx(sheetName, headers, rows), filename.endsWith('.xlsx') ? filename : `${filename}.xlsx`);
}
